/** @jsxRuntime classic */
/** @jsx jsx */
import {css, jsx} from "@emotion/core";
import React from 'react';
import {getAppState, getBootstrapParams} from "../store/AppState";
import {Button, Dialog, FormGroup, Select, TextInput, Toolbar, ToolbarSpacer} from "@ice/components";
import {getAppComponent, getMapComponent} from "../store/Accessor";
import {$t} from "../lang/LangUtil";
import {Dataset} from "../data/Dataset";
import {LayerUtil} from "../map/LayerUtil";
import {DatasetFieldSelect} from "./DatasetFieldSelect";
import {FormContainer} from "./FormContainer";
import {GeoFieldType, LayerType} from "../types";


interface AddLayerDialogProps {
}


interface AddLayerDialogState {
    dataset: Dataset;
    layerName: string;
    fieldId: string;
    layerType: LayerType;
    geoFieldType: GeoFieldType;
}


export class AddLayerDialog extends React.Component<AddLayerDialogProps, AddLayerDialogState> {

    constructor(props: AddLayerDialogProps) {
        super(props);
        this.state = this.prepareState();
    }

    private prepareState = (dataset?: Dataset): AddLayerDialogState => {
        if (!dataset) {
            let datasets = getAppState().getDatasets();
            if (datasets.length > 0) {
                dataset = datasets[0];
            }
        }

        let fieldId = dataset.getFirstFloatFieldDef()?.id ?? dataset.getFirstIntegerFieldDef()?.id;
        let layerType: LayerType = "point";

        if (dataset) {
            let defaults = getBootstrapParams().getDatasetDefaults(dataset.getType());
            if (defaults?.field && dataset.getFieldDefById(defaults?.field)) {
                fieldId = defaults?.field;
            }
            layerType = defaults?.layerType ?? layerType;
        }

        return {
            dataset: dataset,
            layerName: getAppState().getUniqueLayerName(dataset, dataset.getName()),
            fieldId: fieldId,
            layerType: layerType,
            geoFieldType: "geometry"
        };
    }

    private close = () => {
        getAppComponent().hideDialog();
    }

    private setLayerName = (name: string): void => {
        this.setState({layerName: name});
    }

    private addLayer = () => {
        let dataset = this.state.dataset;
        const layerDef = LayerUtil.createLayerDef(dataset.getId(), this.state.layerName ?? dataset.getName(), dataset);
        layerDef.type = this.state.layerType;
        layerDef.geoFieldType = this.state.geoFieldType;
        layerDef.config.colorFieldId = this.state.fieldId;
        layerDef.config.sizeFieldId = this.state.fieldId;

        this.close();
        getAppState().addLayerDef(layerDef);
        getMapComponent().addLayer(layerDef);
        getAppComponent().refresh();
    }

    render = () => {
        const dataset = this.state.dataset;

        const allDatasets = getAppState().getDatasets();

        let field = dataset.getFieldDefById(this.state.fieldId);
        if (!field) {
            field = dataset.getFirstFloatFieldDef() ?? dataset.getFirstIntegerFieldDef();
        }

        const applicableLayerTypes: LayerType[] = ["point", "cluster"];
        if (dataset.supportsGeojson()) {
            applicableLayerTypes.push("geojson");
        }

        const geoFieldTypes: GeoFieldType[] = ["geometry", "country", "state", "county"];

        let isValid = !!this.state.layerName;

        return (
            <Dialog
                onClose={this.close}
                title={$t("createnewlayer")}
                closeControl={true}
                modal={true}
            >
                <div css={css`width: 34rem; overflow: hidden; padding: 1rem`}>
                    <FormContainer>
                        <FormGroup inline label={$t("dataset")}>
                            <Select<Dataset>
                                clearable={false}
                                searchable={false}
                                getValue={(i) => allDatasets[i]}
                                getSelectionLabel={ds => ds.getName()}
                                getOptionLabel={(i) => allDatasets[i].getName()}
                                onChange={(ds: Dataset) => this.setState(this.prepareState(ds))}
                                optionCount={allDatasets.length}
                                value={dataset}
                            />
                        </FormGroup>
                        <FormGroup inline="center" label={$t("layername")} key={`ln_${dataset.getId()}`}>
                            <TextInput defaultValue={this.state.layerName} onChange={(evt) => this.setLayerName(evt.target.value)} />
                        </FormGroup>
                        <FormGroup inline label={$t("layertype")}>
                            <Select<LayerType>
                                clearable={false}
                                searchable={false}
                                getValue={i => applicableLayerTypes[i]}
                                getOptionLabel={i => $t(applicableLayerTypes[i] as any)}
                                getSelectionLabel={def => $t(def as any)}
                                onChange={layerType => this.setState({layerType: layerType as any})}
                                optionCount={applicableLayerTypes.length}
                                value={this.state.layerType}
                            />
                        </FormGroup>
                        {
                            this.state.layerType === "geojson" &&
                            <FormGroup inline label={$t("geofield")}>
                                <Select<GeoFieldType>
                                    clearable={false}
                                    searchable={false}
                                    getValue={i => geoFieldTypes[i]}
                                    getOptionLabel={i => $t(geoFieldTypes[i] as any)}
                                    getSelectionLabel={def => $t(def as any)}
                                    onChange={geoFieldType => this.setState({geoFieldType: geoFieldType as any})}
                                    optionCount={geoFieldTypes.length}
                                    value={this.state.geoFieldType}
                                />
                            </FormGroup>
                        }
                        <DatasetFieldSelect
                            key={`f_${this.state.fieldId}`}
                            label={$t("field")}
                            fields={dataset.getFieldDefs()}
                            value={field}
                            onFieldSelect={fieldDef => this.setState({fieldId: fieldDef.id})}
                        />

                        <Toolbar bordered={false}>
                            <ToolbarSpacer />
                            <Button autoFocus onClick={this.close}>{$t("cancel")}</Button>
                            <Button onClick={this.addLayer} intent="primary" disabled={!isValid}>
                                {$t("addlayer")}
                            </Button>
                        </Toolbar>
                    </FormContainer>
                </div>
            </Dialog>
        );
    }

}
